import React, { createContext, useContext, useState, useEffect } from 'react';
import { superuserAuth } from '../services/superuserApi';

const SuperUserAuthContext = createContext(null);

export const useSuperUserAuth = () => {
  const context = useContext(SuperUserAuthContext);
  if (!context) {
    throw new Error('useSuperUserAuth must be used within a SuperUserAuthProvider');
  }
  return context;
};

export const SuperUserAuthProvider = ({ children }) => {
  const [superUser, setSuperUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clear any potentially corrupted localStorage data first
    try {
      const storedUser = localStorage.getItem('superuser');
      const storedToken = localStorage.getItem('superuser_token');
      
      // Only proceed if both exist and storedUser looks like JSON
      if (storedToken && storedUser && storedUser.length > 0) {
        // Additional validation - must start and end with braces and be reasonable length
        if (storedUser.startsWith('{') && storedUser.endsWith('}') && storedUser.length > 10 && storedUser.length < 10000) {
          try {
            const parsedUser = JSON.parse(storedUser);
            // Validate the parsed object has expected properties
            if (parsedUser && typeof parsedUser === 'object' && parsedUser.email) {
              setSuperUser(parsedUser);
            } else {
              console.warn('Invalid superuser object structure, clearing localStorage');
              localStorage.removeItem('superuser_token');
              localStorage.removeItem('superuser');
            }
          } catch (parseError) {
            console.error('JSON parse error for superuser, clearing data:', parseError);
            localStorage.removeItem('superuser_token');
            localStorage.removeItem('superuser');
          }
        } else {
          console.warn('Invalid superuser data format, clearing localStorage');
          localStorage.removeItem('superuser_token');
          localStorage.removeItem('superuser');
        }
      } else if (storedUser && storedUser.length > 0) {
        // storedUser exists but looks invalid
        console.warn('Potentially corrupted superuser data detected, clearing localStorage');
        localStorage.removeItem('superuser_token');
        localStorage.removeItem('superuser');
      }
    } catch (error) {
      console.error('Error in superuser auth initialization, clearing data:', error);
      localStorage.removeItem('superuser_token');
      localStorage.removeItem('superuser');
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await superuserAuth.login(email, password);
      const { token, ...user } = response.data;
      
      localStorage.setItem('superuser_token', token);
      localStorage.setItem('superuser', JSON.stringify(user));
      setSuperUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erro ao fazer login' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('superuser_token');
    localStorage.removeItem('superuser');
    setSuperUser(null);
  };

  const value = {
    superUser,
    loading,
    login,
    logout,
    isAuthenticated: !!superUser,
  };

  return (
    <SuperUserAuthContext.Provider value={value}>
      {children}
    </SuperUserAuthContext.Provider>
  );
};

export default SuperUserAuthContext;
